import { useEffect, useRef, useState } from "react";
import {
  usePublishProject,
  usePublishStatus,
} from "@vorloop/api-client-react";

/**
 * Publish modal. Stub-grade for now — POSTing /publish only records the
 * slug + activeHash. Real Cloudflare Workers + asset upload pipeline
 * will plug in here without changing the UI.
 */
export interface PublishModalProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onClose: () => void;
}

export function PublishModal({ projectId, projectName, open, onClose }: PublishModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const status = usePublishStatus(projectId, { enabled: open });
  const publish = usePublishProject(projectId);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  if (!open) return null;

  const url = status.data?.url ?? publish.data?.url ?? null;

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Publish"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(2px)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
      }}
    >
      <div
        ref={ref}
        style={{
          width: "min(460px, 92vw)",
          background: "var(--bg-panel)",
          border: "1px solid var(--border-strong)",
          borderRadius: 14,
          padding: 22,
          display: "flex",
          flexDirection: "column",
          gap: 16,
          color: "var(--text-primary)",
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
        }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>Publish {projectName}</h2>
            <p style={{ margin: "4px 0 0", color: "var(--text-tertiary)", fontSize: 12 }}>
              Get a public URL anyone can open.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-tertiary)",
              cursor: "pointer",
              padding: 4,
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </header>

        {!url && (
          <button
            type="button"
            onClick={() => publish.mutate()}
            disabled={publish.isPending}
            style={{
              padding: "12px 16px",
              background: publish.isPending
                ? "var(--bg-active)"
                : "linear-gradient(135deg,#8B5CF6,#6366F1)",
              color: publish.isPending ? "var(--text-tertiary)" : "#fff",
              border: "none",
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 14,
              cursor: publish.isPending ? "wait" : "pointer",
            }}
          >
            {publish.isPending ? "Publishing…" : "✦ Publish"}
          </button>
        )}

        {url && (
          <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Live URL
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <code
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: "var(--bg-canvas)",
                  border: "1px solid var(--border-default)",
                  borderRadius: 8,
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  color: "var(--text-secondary)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {url}
              </code>
              <button
                type="button"
                onClick={copy}
                style={{
                  padding: "0 14px",
                  background: "var(--bg-active)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-default)",
                  borderRadius: 8,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button
              type="button"
              onClick={() => publish.mutate()}
              disabled={publish.isPending}
              style={{
                alignSelf: "flex-start",
                padding: "6px 12px",
                background: "transparent",
                color: "var(--text-secondary)",
                border: "1px solid var(--border-default)",
                borderRadius: 8,
                fontSize: 12,
                cursor: publish.isPending ? "wait" : "pointer",
              }}
            >
              {publish.isPending ? "Re-publishing…" : "Re-publish current state"}
            </button>
          </section>
        )}

        <div
          style={{
            padding: 10,
            background: "rgba(16,185,129,0.08)",
            border: "1px solid rgba(16,185,129,0.3)",
            borderRadius: 8,
            color: "#34d399",
            fontSize: 11,
            lineHeight: 1.5,
          }}
        >
          Each publish snapshots your project source into a versioned
          bundle and serves it read-only. Re-publish to update. Custom
          domains + wildcard subdomains on the edge land later.
        </div>

        {publish.error && (
          <div
            style={{
              padding: 10,
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: 8,
              color: "var(--accent-red)",
              fontSize: 12,
            }}
          >
            {publish.error.message}
          </div>
        )}
      </div>
    </div>
  );
}
